/**
 * Trustpilot Drawer
 *
 * Handles the slide-in/out animation for the Trustpilot reviews modal.
 * Shares the same animation pattern as the cart drawer.
 */

// Track drawer state
let drawerState: "closed" | "opening" | "open" | "closing" = "closed";
let tpDrawerInitialized = false;

const getTpOverlay = (): HTMLElement | null => document.getElementById("tpOverlay");
const getTpContainer = (): HTMLElement | null => document.getElementById("tpContainer");

/**
 * Opens the Trustpilot drawer with animation
 */
function openTpDrawer(event?: Event) {
  if (event) event.preventDefault();

  initTpDrawer();

  const tpOverlay = getTpOverlay();
  const tpContainer = getTpContainer();
  if (!tpOverlay || !tpContainer) return;

  if (drawerState === "opening" || drawerState === "open") return;
  if (drawerState === "closing") {
    // Wait for close animation to finish, then reopen
    setTimeout(() => openTpDrawer(), 310);
    return;
  }

  drawerState = "opening";

  tpOverlay.classList.remove("pointer-events-none", "hidden");

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      tpOverlay.classList.remove("bg-black/0");
      tpOverlay.classList.add("bg-black/30");
      tpContainer.classList.remove("translate-y-full", "md:translate-x-full");

      setTimeout(() => {
        drawerState = "open";
        document.dispatchEvent(new CustomEvent("ek:tp-drawer-open"));
      }, 300);
    });
  });

  document.body.style.overflow = "hidden";
}

/**
 * Closes the Trustpilot drawer with animation
 */
function closeTpDrawer() {
  if (drawerState === "closed" || drawerState === "closing") return;

  drawerState = "closing";

  const tpOverlay = getTpOverlay();
  const tpContainer = getTpContainer();
  if (!tpOverlay || !tpContainer) {
    drawerState = "closed";
    return;
  }

  tpContainer.classList.add("translate-y-full", "md:translate-x-full");
  tpOverlay.classList.remove("bg-black/30");
  tpOverlay.classList.add("bg-black/0");

  setTimeout(() => {
    tpOverlay.classList.add("hidden", "pointer-events-none");
    document.body.style.overflow = "";
    drawerState = "closed";
  }, 300);
}

/**
 * Initializes the Trustpilot drawer event listeners
 */
function initTpDrawer() {
  const tpOverlayEl = getTpOverlay();
  const tpContainerEl = getTpContainer();

  if (!tpOverlayEl || !tpContainerEl) return;

  if (!tpDrawerInitialized) {
    tpDrawerInitialized = true;

    // Close when clicking overlay background
    tpOverlayEl.addEventListener("click", (event) => {
      if (event.target === tpOverlayEl) {
        closeTpDrawer();
      }
    });

    // Close when clicking the down arrow (mobile) - scoped to Trustpilot overlay
    const closeArrow = tpOverlayEl.querySelector(".icon-hover-bubble");
    if (closeArrow) {
      closeArrow.addEventListener("click", closeTpDrawer);
    }

    // Close when clicking the desktop back arrow - scoped to Trustpilot overlay
    const desktopArrowBack = tpOverlayEl.querySelector("#desktopArrowBack");
    if (desktopArrowBack) {
      desktopArrowBack.addEventListener("click", closeTpDrawer);
    }

    // Close on Escape key
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && drawerState === "open") {
        closeTpDrawer();
      }
    });
  }

  // Open when clicking product stars (PDP links are static; Berater stars bind in TS)
  const openReviewsLinks = document.querySelectorAll(".open-reviews");
  for (const openReviews of openReviewsLinks) {
    if (openReviews instanceof HTMLElement && openReviews.dataset.tpDrawerInit === "true") continue;
    if (openReviews instanceof HTMLElement) openReviews.dataset.tpDrawerInit = "true";
    openReviews.addEventListener("click", (e) => {
      e.preventDefault();
      openTpDrawer(e);
    });
  }
}

// Initialize on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initTpDrawer);
} else {
  initTpDrawer();
}

// Re-initialize on section load (Theme Editor)
document.addEventListener("shopify:section:load", () => {
  initTpDrawer();
});

// Expose globally for external access
(window as any).openTpDrawer = openTpDrawer;
(window as any).closeTpDrawer = closeTpDrawer;

export { openTpDrawer, closeTpDrawer, initTpDrawer };
